import React, { useMemo, useRef, useState, useEffect } from "react";
import "./AssigneeSelector.css"
export default function AssigneeSelector({
  members = [],                 // [{id, first_name, last_name, email, role}]
  value = [],                   // selected userIds
  onChange,                     // (ids:number[]) => void
  placeholder = "Type a name or email…",
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  // Build display name once
  const withNames = useMemo(
    () =>
      members.map(m => ({
        ...m,
        name: [m.first_name, m.last_name].filter(Boolean).join(" ") || m.email,
      })),
    [members]
  );

  // Filter suggestions
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const selectedSet = new Set(value);
    const pool = withNames.filter(m => !selectedSet.has(m.id));
    if (!q) return pool.slice(0, 8);
    return pool
      .filter(m =>
        m.name.toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [query, value, withNames]);

  const add = (userId) => {
    if (!value.includes(userId)) onChange?.([...value, userId]);
    setQuery("");
    setOpen(false);
    setActiveIdx(0);
    inputRef.current?.focus();
  };

  const remove = (userId) => {
    onChange?.(value.filter(id => id !== userId));
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "Backspace" && !query && value.length) {
      // delete last chip
      onChange?.(value.slice(0, -1));
      return;
    }
    if (!open && ["ArrowDown", "Enter"].includes(e.key)) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = suggestions[activeIdx];
      if (target) add(target.id);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Click outside closes
  useEffect(() => {
    const handler = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);

  return (
    <div className="assignee-typeahead" ref={wrapRef}>
      <div
        className={`assignee-control ${open ? "focus" : ""}`}
        onClick={() => inputRef.current?.focus()}
      >
        {/* Chips */}
        {value.map((id) => {
          const m = withNames.find(x => x.id === id);
          if (!m) return null;
          return (
            <span key={id} className="assignee-chip" title={m.email}>
              {m.name}
              <button
                type="button"
                className="chip-x"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(id);
                }}
                aria-label={`Remove ${m.name}`}
              >
                ×
              </button>
            </span>
          );
        })}

        {/* Input */}
        <input
        type="search"
          ref={inputRef}
          name="assigneeQuery"  
          className="assignee-input"
          value={query}
          autoComplete="new-password"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          inputMode="search"      // nicer mobile keyboard
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          onFocus={() => setOpen(true)}
          placeholder={value.length ? "" : placeholder}
        />
      </div>

      {open && suggestions.length > 0 && (
        <div className="assignee-menu" role="listbox">
          {suggestions.map((m, idx) => (
            <button
              key={m.id}
              type="button"
              className={`assignee-item ${idx === activeIdx ? "active" : ""}`}
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => add(m.id)}
              role="option"
              aria-selected={idx === activeIdx}
              title={m.email}
            >
              <div className="assignee-avatar">{m.name.charAt(0).toUpperCase()}</div>
              <div className="assignee-meta">
                <div className="assignee-name">{m.name}</div>
                <div className="assignee-email">{m.email}</div>
              </div>
              <span className={`assignee-role ${m.role === "manager" ? "mgr" : ""}`}>{m.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
